import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Search, Loader2, AlertCircle, ArrowRight, Building2, Home as HomeIcon, Briefcase, Hash, CheckCircle2, HelpCircle, ShieldCheck, Clock } from "lucide-react";
import VoltIcon from "./VoltIcon";
import { api } from "../lib/api";
import { trackAddressSearch } from "../lib/ga";
import { enrichWithRentCast } from "../lib/rentcastEnrich";

const STATUS_TO_LABEL = {
  Active: { es: "Activo", en: "Active", tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  Inactive: { es: "Inactivo", en: "Inactive", tone: "bg-amber-100 text-amber-700 border-amber-200" },
  "De-Energized": { es: "Desconectado", en: "De-energized", tone: "bg-rose-100 text-rose-700 border-rose-200" },
};

// Texas ZIP regex (73xxx-79xxx, 88xxx El Paso)
const TX_ZIP_RE = /\b(7\d{4}|88\d{3})\b/;

// Detect service type from address + premise type
function detectServiceType(addressText, premiseType) {
  const txt = (addressText || "").toLowerCase();
  const hasUnit = /#\s*\w|\bapt\.?\s*\w|\bsuite\s*\w|\bunit\s*\w|\bste\.?\s*\w/i.test(txt);
  const premise = (premiseType || "").toLowerCase();
  if (premise.includes("non-residential")) return "commercial";
  if (premise.includes("residential") && hasUnit) return "apartments";
  if (premise.includes("residential")) return "residential";
  if (hasUnit) return "apartments";
  return "residential";
}

// Rotating placeholder examples
const ROTATING_EXAMPLES = [
  "6722 Paris St, Houston, TX 77021",
  "440 Louisiana St, Houston 77002",
  "1234 Main St, Dallas, TX 75201",
  "789 Oak Ave, Austin, TX 78701",
  "555 Commerce St, Fort Worth 76102",
];

export default function AddressHero() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [address, setAddress] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [searched, setSearched] = useState(false);
  const [matchStatus, setMatchStatus] = useState(""); // "exact" | "good" | "partial" | "wrong_zip" | "none"
  const [parsedZip, setParsedZip] = useState("");
  const [error, setError] = useState("");
  const [exampleIdx, setExampleIdx] = useState(0);
  const [inputMode, setInputMode] = useState("address"); // "address" | "esiid"
  const [esiid, setEsiid] = useState("");
  const [esiidZip, setEsiidZip] = useState("");
  const [showZipHelp, setShowZipHelp] = useState(false);
  const debounceRef = useRef();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  // Rotate placeholder
  useEffect(() => {
    const id = setInterval(() => {
      setExampleIdx((i) => (i + 1) % ROTATING_EXAMPLES.length);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  // Auto-scroll + focus when arriving via #calcular hash or ?focus=address
  useEffect(() => {
    const wantsFocus =
      location.hash === "#calcular" ||
      new URLSearchParams(location.search).get("focus") === "address";
    if (!wantsFocus) return;
    const id = setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      inputRef.current?.focus({ preventScroll: true });
    }, 250);
    return () => clearTimeout(id);
  }, [location.hash, location.search]);

  const parseZip = (text) => {
    const m = text.match(TX_ZIP_RE);
    return m ? m[0] : "";
  };

  const triggerLookup = async (val) => {
    const v = (val ?? address).trim();
    if (v.length < 3) return;
    setSearching(true);
    setError("");
    // Funnel step 1/5 — fire ONCE per intent (when API call starts) so we
    // don't spam GA with every debounce. Identifies the home hero source.
    trackAddressSearch({ source: "home_hero", hasZip: false });
    try {
      // Search-as-you-type: ComparePower returns matches by free-text address.
      // No ZIP needed — user picks a candidate which carries the canonical ZIP.
      const { data } = await api.post("/comparepower/esiid_lookup", {
        address: v,
        city: "",
        state: "TX",
        zip: "",
      });
      setCandidates(data?.candidates || []);
      setMatchStatus(data?.match_status || "");
      setParsedZip(data?.parsed?.zip || "");
      // Silent: never set an error — the UX is autocomplete-only, no scary banners.
    } catch (e) {
      setCandidates([]);
      setMatchStatus("none");
      setParsedZip("");
      // Silent fail — no scary error. User keeps typing.
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  const onChange = (val) => {
    setAddress(val);
    setError("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length >= 3) {
      // Aggressive autocomplete: 220ms — user feels it's instant.
      debounceRef.current = setTimeout(() => triggerLookup(val), 220);
    } else {
      setCandidates([]);
      setSearched(false);
    }
  };

  const handlePick = async (c) => {
    const detectedType = detectServiceType(c.address, c.premise_type);
    // Trust the canonical ZIP from ComparePower (c.zip), fall back to user input only if empty.
    const zip = c.zip || parseZip(c.address) || parseZip(address) || "";

    // RentCast enrichment — silent, optional, never blocks the flow. Same
    // call used by Address.jsx and CityAddressBar so the entire site routes
    // commercial properties / apartments consistently from any entry point.
    const { routedType, profileId } = await enrichWithRentCast({
      address: c.address,
      city: c.city || "",
      zip,
      type: detectedType,
    });

    // Persist
    localStorage.setItem("velcor_zip", zip);
    localStorage.setItem("velcor_service_type", routedType);
    localStorage.setItem("velcor_service_address", c.address);
    localStorage.setItem("velcor_esiid", c.esiid || "");
    localStorage.setItem("velcor_utility", c.utility_name || "");
    if (c.city) localStorage.setItem("velcor_city", c.city);

    const q = new URLSearchParams();
    q.set("zip", zip);
    q.set("type", routedType);
    q.set("address", c.address);
    if (c.city) q.set("city", c.city);
    if (c.esiid) q.set("esiid", c.esiid);
    if (c.utility_name) q.set("utility", c.utility_name);
    if (profileId) q.set("profile", profileId);

    navigate(`/${routedType}?${q.toString()}`);
  };

  const ServiceIcon = ({ type, className = "" }) => {
    if (type === "apartments") return <Building2 className={className} />;
    if (type === "commercial") return <Briefcase className={className} />;
    return <HomeIcon className={className} />;
  };

  // ESIID validation: 17-22 digits (Texas ESI ID spec)
  const cleanEsiid = (v) => (v || "").replace(/\D/g, "");
  const isValidEsiid = cleanEsiid(esiid).length >= 17 && cleanEsiid(esiid).length <= 22;
  const isValidEsiidZip = /^\d{5}$/.test(esiidZip);

  const submitEsiid = (e) => {
    if (e) e.preventDefault();
    const eid = cleanEsiid(esiid);
    if (!isValidEsiid || !isValidEsiidZip) return;

    // Persist — we skip service-type auto-detection here (user can change on plan page)
    localStorage.setItem("velcor_zip", esiidZip);
    localStorage.setItem("velcor_service_type", "residential");
    localStorage.setItem("velcor_esiid", eid);
    localStorage.removeItem("velcor_service_address");
    localStorage.removeItem("velcor_utility");
    localStorage.removeItem("velcor_city");

    const q = new URLSearchParams();
    q.set("zip", esiidZip);
    q.set("type", "residential");
    q.set("esiid", eid);
    navigate(`/residential?${q.toString()}`);
  };

  return (
    <div ref={containerRef} id="calcular" className="relative w-full max-w-[760px] mx-auto scroll-mt-24" data-testid="address-hero">
      {inputMode === "address" && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          triggerLookup();
        }}
        className="glass-strong rounded-[26px] md:rounded-full grid md:flex md:items-center gap-3 p-3 md:p-2 md:pl-5 shadow-[0_24px_60px_rgba(15,23,42,0.10)]"
        style={{ borderColor: "rgba(15,23,42,0.08)" }}
      >
        <span
          className="hidden md:grid place-items-center rounded-full"
          style={{ width: 52, height: 52, background: "rgba(47,155,67,0.12)", color: "#2f7a3a" }}
        >
          <MapPin className="w-5 h-5" />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={address}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("home.heroPlaceholder", { example: ROTATING_EXAMPLES[exampleIdx] })}
          className="flex-1 min-w-0 bg-transparent outline-none px-3 py-3 md:py-0 text-[16px] md:text-[17px] font-bold text-velcor-ink placeholder:text-slate-400 placeholder:font-semibold min-h-[52px]"
          autoComplete="street-address"
          data-testid="hero-address-input"
        />
        <button type="submit" className="btn-green min-h-[54px] px-6" disabled={searching} data-testid="hero-search">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          {searching ? t("home.heroSearching") : t("home.heroSearch")}
        </button>
      </form>
      )}

      {inputMode === "esiid" && (
        <form
          onSubmit={submitEsiid}
          className="glass-strong rounded-[26px] p-3 md:p-4 shadow-[0_24px_60px_rgba(15,23,42,0.10)]"
          data-testid="hero-esiid-form"
        >
          <div className="grid md:grid-cols-[1fr_160px_auto] gap-2 md:gap-3">
            <label className="relative flex items-center gap-2 rounded-full bg-white/70 border border-slate-200 px-4">
              <Hash className="w-4 h-4 text-velcor-green shrink-0" />
              <input
                type="text"
                inputMode="numeric"
                value={esiid}
                onChange={(e) => setEsiid(e.target.value)}
                placeholder={t("home.heroEsiidPlaceholder")}
                className="flex-1 min-w-0 bg-transparent outline-none py-3 text-[16px] font-bold text-velcor-ink placeholder:text-slate-400 placeholder:font-semibold min-h-[52px]"
                data-testid="hero-esiid-input"
                maxLength={25}
              />
              {esiid && (
                isValidEsiid ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                ) : (
                  <span className="text-[11px] font-extrabold text-slate-400 shrink-0">
                    {cleanEsiid(esiid).length}/17+
                  </span>
                )
              )}
            </label>

            <label className="flex items-center gap-2 rounded-full bg-white/70 border border-slate-200 px-4">
              <MapPin className="w-4 h-4 text-velcor-green shrink-0" />
              <input
                type="text"
                inputMode="numeric"
                value={esiidZip}
                onChange={(e) => setEsiidZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder={t("home.heroZipPlaceholder")}
                className="flex-1 min-w-0 bg-transparent outline-none py-3 text-[16px] font-bold text-velcor-ink placeholder:text-slate-400 placeholder:font-semibold min-h-[52px]"
                data-testid="hero-esiid-zip"
                maxLength={5}
              />
            </label>

            <button
              type="submit"
              className="btn-green min-h-[54px] px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!isValidEsiid || !isValidEsiidZip}
              data-testid="hero-esiid-submit"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              {t("home.heroEsiidContinue")}
            </button>
          </div>
          <p className="mt-3 text-slate-500 text-[12px] font-semibold leading-relaxed">
            {t("home.heroEsiidHint")}
          </p>
        </form>
      )}

      {/* Proof chips removed per design request */}

      {/* No error banner during address search — UX rule: only suggestions,
          never scary messages. If the upstream is down we silently show no
          results and let the user keep typing. */}

      {/* Live results — only for address mode */}
      {inputMode === "address" && (
      <AnimatePresence>
        {/* No "no matches" banner — autocomplete UX: keep typing → results appear,
            no scary errors. If literally nothing returns, the input stays clean
            and the user just keeps refining. */}
        {false && searched && candidates.length === 0 && !searching && null}

        {candidates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-5 glass-strong rounded-[24px] p-4 md:p-5"
            data-testid="hero-results"
          >
            {/* No "partial match" warning — autocomplete shows live results;
                if the user wanted a different address, they keep typing. */}
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h3 className="font-black text-[14px] text-velcor-ink">
                <VoltIcon className="inline w-4 h-4 text-velcor-green mr-1.5" strokeWidth={1.5} />
                {t("home.heroPickAddress")}
                <span className="text-slate-400 font-bold ml-1.5">({candidates.length})</span>
              </h3>
              <span className="text-[11.5px] text-slate-500 font-bold">{t("home.heroAutoDetect")}</span>
            </div>

            <div className="grid gap-2 max-h-[420px] overflow-y-auto no-scrollbar">
              {candidates.map((c) => {
                const dtype = detectServiceType(c.address, c.premise_type);
                const statusInfo = STATUS_TO_LABEL[c.status] || STATUS_TO_LABEL.Active;
                return (
                  <button
                    key={c.esiid}
                    type="button"
                    onClick={() => handlePick(c)}
                    data-testid={`hero-candidate-${c.esiid}`}
                    className="text-left rounded-2xl px-4 py-3 border-2 transition-all bg-white border-slate-200 hover:border-velcor-green hover:shadow-[0_18px_36px_rgba(47,122,58,0.14)] hover:-translate-y-0.5 group"
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="grid place-items-center rounded-xl shrink-0"
                        style={{ width: 40, height: 40, background: "rgba(47,155,67,0.12)", color: "#2f7a3a" }}
                      >
                        <ServiceIcon type={dtype} className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-velcor-ink text-[14.5px] truncate">{c.address}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className="pill px-2 py-0.5 text-[10.5px] font-extrabold bg-velcor-green/10 text-velcor-green border border-velcor-green/20">
                            {t(`home.service.${dtype}`)}
                          </span>
                          <span className="pill px-2 py-0.5 text-[10.5px] font-extrabold bg-slate-100 text-slate-700 border border-slate-200">
                            {c.utility_name}
                          </span>
                          <span className={`pill px-2 py-0.5 text-[10.5px] font-extrabold border ${statusInfo.tone}`}>
                            {statusInfo[lang]}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-velcor-green transition-colors shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      )}

      {/* ZIP-help modal */}
      {showZipHelp && <ZipHelpModal onClose={() => setShowZipHelp(false)} />}
    </div>
  );
}


/* ---------- ZIP Help modal ---------- */
function ZipHelpModal({ onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start md:items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="ziphelp-modal"
    >
      <div
        className="w-full max-w-[560px] bg-white rounded-2xl shadow-2xl my-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 md:p-6 border-b border-slate-100">
          <div>
            <span className="badge-pill mb-2">{t("ziphelp.kicker")}</span>
            <h3 className="display text-velcor-ink leading-[1.1]" style={{ fontSize: 24 }}>
              {t("ziphelp.title")}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid place-items-center w-9 h-9 rounded-full hover:bg-slate-100"
            aria-label="Close"
          >
            <span className="text-slate-500 text-xl leading-none">×</span>
          </button>
        </div>
        <div className="p-5 md:p-6 space-y-4">
          <p className="text-slate-600 text-[14.5px] leading-relaxed">{t("ziphelp.lead")}</p>
          <ol className="space-y-3">
            {[1, 2, 3, 4].map((n) => (
              <li key={n} className="flex items-start gap-3">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-velcor-green text-white font-extrabold text-[12.5px] shrink-0">
                  {n}
                </span>
                <span className="text-slate-700 text-[14px] leading-relaxed pt-0.5">
                  {t(`ziphelp.step${n}`)}
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-5 rounded-2xl bg-velcor-green/5 border border-velcor-green/20 p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-velcor-green mt-0.5 shrink-0" strokeWidth={2.2} />
            <div>
              <div className="font-extrabold text-velcor-ink text-[13.5px]">{t("ziphelp.tipTitle")}</div>
              <p className="text-slate-600 text-[13px] leading-relaxed mt-0.5">{t("ziphelp.tipBody")}</p>
            </div>
          </div>
        </div>
        <div className="p-5 md:p-6 border-t border-slate-100 flex flex-wrap gap-3 justify-end">
          <a
            href="/shop"
            className="inline-flex items-center h-11 px-5 rounded-full border-2 border-slate-200 hover:border-velcor-green hover:text-velcor-green text-slate-700 font-extrabold text-[13px] transition-all"
          >
            {t("ziphelp.continueZip")}
          </a>
          <button
            type="button"
            onClick={onClose}
            className="btn-green h-11 px-5 text-[13px]"
          >
            {t("ziphelp.gotIt")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProofChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-white/85 backdrop-blur-sm ring-1 ring-slate-200 text-[11.5px] font-extrabold text-slate-700">
      <Icon className="w-3.5 h-3.5 text-velcor-green" strokeWidth={2.4} />
      {label}
    </span>
  );
}
